import { HubConnectionBuilder, HttpTransportType, LogLevel } from '@microsoft/signalr'
import { ref } from 'vue'
import { API_CONFIG, ensureFreshToken, setConnectionIdProvider } from '../utils/api.js'

/**
 * La connessione all'hub del Backend: una sola per scheda, aperta finché l'utente è dentro.
 *
 * Ha preso il posto di quattro timer (lista ogni 2 minuti, scadenze ogni 5, blocchi ogni 20 secondi
 * e il battito). Con una differenza che va tenuta a mente: un timer che non parte si nota subito
 * perché i dati restano fermi, una connessione caduta invece assomiglia a "non sta succedendo
 * niente". Per questo `liveState` esiste e la app bar lo mostra.
 */

/** idle | connecting | online | offline — legato all'indicatore nella app bar. */
export const liveState = ref('idle')

/**
 * Canale giù per davvero, non per un attimo: è questo che blocca l'interfaccia, non `liveState`.
 *
 * Senza la finestra di tolleranza il blocco comparirebbe a ogni singhiozzo della rete — coperchio
 * del portatile, cambio di Wi-Fi, un riavvio del Backend in sviluppo — e in quei casi la
 * riconnessione automatica rimedia da sola in un paio di secondi.
 */
export const liveDown = ref(false)

const DOWN_GRACE_MS = 8000
let downTimer = null

function setState(next) {
  liveState.value = next

  if (next === 'online' || next === 'idle') {
    clearTimeout(downTimer)
    downTimer = null
    liveDown.value = false
    return
  }

  // 'connecting' e 'offline': si aspetta prima di dichiarare il canale caduto.
  if (!liveDown.value && downTimer === null) {
    downTimer = setTimeout(() => { downTimer = null; liveDown.value = true }, DOWN_GRACE_MS)
  }
}

let connection = null
let onEventHandler = null
let onResyncHandler = null
// Un solo tentativo di riapertura alla volta: la chiusura e l'errore di `start` porterebbero
// altrimenti a due catene di timer che si moltiplicano a ogni giro.
let reopening = false
let reopenTimer = null

// I gruppi dei processi aperti: dopo una riconnessione l'iscrizione è persa e va rifatta.
const joined = new Set()

// Attesa crescente, e poi costante: la scheda può restare aperta tutta la notte, quindi non ci si
// arrende mai, ma nemmeno si martella il server quando è giù davvero.
const retryPolicy = {
  nextRetryDelayInMilliseconds: (ctx) =>
    [0, 2000, 5000, 10000, 30000][Math.min(ctx.previousRetryCount, 4)]
}

export function connectionId() {
  return connection?.connectionId || null
}

export function isLive() {
  return liveState.value === 'online'
}

/**
 * Apre la connessione. `onEvent(name, payload)` riceve gli eventi, `resync()` viene chiamata a ogni
 * (ri)connessione riuscita.
 */
export async function startLive({ onEvent, resync }) {
  if (connection) return connection

  onEventHandler = onEvent
  onResyncHandler = resync

  connection = new HubConnectionBuilder()
    .withUrl(`${API_CONFIG.backend}/hubs/live`, {
      accessTokenFactory: () => ensureFreshToken(),
      withCredentials: false,
      transport: HttpTransportType.WebSockets | HttpTransportType.LongPolling
    })
    .withAutomaticReconnect(retryPolicy)
    .configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning)
    .build()

  connection.on('projectChanged', (e) => onEventHandler?.('projectChanged', e))
  connection.on('lockChanged', (e) => onEventHandler?.('lockChanged', e))

  connection.onreconnecting(() => { setState('connecting') })
  connection.onreconnected(() => { afterConnect() })
  connection.onclose(() => {
    setState('offline')
    // withAutomaticReconnect si arrende dopo l'ultimo tentativo: da lì in poi ci pensiamo noi.
    scheduleRestart(30000)
  })

  // Da ora le scritture portano l'id della connessione, così il Backend non ci rimanda la nostra eco.
  setConnectionIdProvider(connectionId)

  await restart()
  return connection
}

function scheduleRestart(delay) {
  if (reopening) return
  reopening = true
  clearTimeout(reopenTimer)
  reopenTimer = setTimeout(() => { reopening = false; restart() }, delay)
}

async function restart() {
  if (!connection || connection.state !== 'Disconnected') return
  try {
    setState('connecting')
    await connection.start()
    await afterConnect()
  } catch (err) {
    console.warn('Hub non raggiungibile, nuovo tentativo fra 5 s', err)
    setState('offline')
    scheduleRestart(5000)
  }
}

/**
 * Dopo ogni connessione riuscita. Il recupero è obbligatorio: SignalR non ritrasmette ciò che è
 * successo mentre eravamo giù, non esiste alcun buffer per gli invii a un gruppo. Senza questo, una
 * disconnessione di trenta secondi lascerebbe la lista sbagliata per sempre.
 */
async function afterConnect() {
  setState('online')

  for (const id of joined) {
    try { await connection.invoke('JoinProject', id) } catch { /* si riprova alla prossima */ }
  }

  try { await onResyncHandler?.() } catch (err) { console.error('Risincronizzazione fallita', err) }
}

export async function stopLive() {
  clearTimeout(reopenTimer)
  reopening = false
  joined.clear()
  setConnectionIdProvider(null)
  const current = connection
  connection = null // prima, così onclose non tenta di riaprire
  try { await current?.stop() } catch { /* stava già chiudendo */ }
  setState('idle')
}

/** Entra nel gruppo di un processo: serve a ricevere il progresso degli step di verifiche altrui. */
export async function joinProject(projectId) {
  if (!projectId) return
  joined.add(projectId)
  if (connection?.state === 'Connected') {
    try { await connection.invoke('JoinProject', projectId) } catch { /* al prossimo giro */ }
  }
}

export async function leaveProject(projectId) {
  if (!projectId) return
  joined.delete(projectId)
  if (connection?.state === 'Connected') {
    try { await connection.invoke('LeaveProject', projectId) } catch { /* irrilevante */ }
  }
}
